import { MeasurementQuantityEnum } from 'domains/measurementDomain';
import {
  DeleteTwoTone,
  MinusCircleTwoTone,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  Affix,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  PageHeader,
  Popconfirm,
  Row,
  Select,
  Switch,
  Tabs,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxInputImage } from 'components';
import { ArrowLeftIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { Headline, SectionHeading } from 'components/RevxTypography';
import { ServiceOptionTypeEnum } from 'domains/jobconfigDomain';
import { isPlatformOrg } from 'domains/orgDomain';
import {
  ServiceOptionTypeOptions,
  SvcOption,
  SvcOptionWithId,
  UOMCoefficientEnum,
  UOMCoefficientOptions,
  UOMOptions,
} from 'domains/svcDomain';
import { useOrgId } from 'hooks/useOrg';
import { isEmpty, uniqueId } from 'lodash-es';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import {
  ADMIN_DETAIL_AFFIX_TOP,
  PLATFORM_FORM_ITEM_LAYOUT,
} from 'utils/constants';
import { RULE_INTEGER, RULE_NUMBER, RULE_TEXT_LEN_255 } from 'utils/validators';

const { TabPane } = Tabs;

/**
 * What an option's values measure. Tagging one makes it render in the viewer's measurement system;
 * NONE (the default) leaves it exactly as entered, which is right for counts, positions and
 * colours - a "HOLE NUMBER" of 3 must never become 76.2.
 */
const VALUE_UOM_OPTIONS: { value: MeasurementQuantityEnum; labelKey: string }[] = [
  { value: MeasurementQuantityEnum.NONE, labelKey: 'platform.svc.form.valueUomNone' },
  { value: MeasurementQuantityEnum.LENGTH, labelKey: 'platform.svc.form.valueUomLength' },
  { value: MeasurementQuantityEnum.CALIPER, labelKey: 'platform.svc.form.valueUomCaliper' },
  { value: MeasurementQuantityEnum.WEIGHT, labelKey: 'platform.svc.form.valueUomWeight' },
  { value: MeasurementQuantityEnum.AREA, labelKey: 'platform.svc.form.valueUomArea' },
  { value: MeasurementQuantityEnum.VOLUME, labelKey: 'platform.svc.form.valueUomVolume' },
];

const SVCItemForm = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const saving = useAppSelector(
    (state) => state.loading.effects.svc.createBaseSvc
  );
  const deleting = useAppSelector(
    (state) => state.loading.effects.svc.deleteSVC
  );
  const { curItem, viewingSvc } = useAppSelector((state) => state.svc);

  const svcTypes = useAppSelector((state) => state.svc.svcTypes);
  const [activeTabKey, setActiveTabKey] = useState('basic');
  const orgId = useOrgId();
  const isBase = isPlatformOrg({ id: orgId });

  const onClose = () => {
    dispatch.svc.setState({
      curItem: undefined,
      creatingSvc: false,
      viewingSvc: false,
    });
  };
  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e: any) {
      const firstErrField = document.querySelector(
        `#${e.errorFields[0].name[0]}`
      );
      if (firstErrField?.scrollIntoView) {
        setTimeout(() => {
          firstErrField?.scrollIntoView();
        }, 1000);
      }

      if (activeTabKey !== 'basic') {
        setActiveTabKey('basic');
      }
      return;
    }

    if (curItem?.id) {
      await dispatch.svc.updateSvc({
        ...curItem,
        ...values,
        svcOptions: collectOptions(svcOptions),
        options: collectOptions(svcOptions),
        parameterLabels: ((values.joinedParameterLabels || '') as string)
          .split(',')
          .map((s) => s.trim())
          .filter((s) => !isEmpty(s)),
      });
    } else {
      await dispatch.svc.createBaseSvc({
        ...curItem,
        ...values,
        isBase: isPlatformOrg({ id: orgId }),
        svcOptions: collectOptions(svcOptions),
        options: collectOptions(svcOptions),
        materialUom: values.materialUom || 1,
        materialUomCoefficient: values.materialUomCoefficient || 'JOB_QUANTITY',
        hasMaterial: values.hasMaterial || false,
        parameterLabels: ((values.joinedParameterLabels || '') as string)
          .split(',')
          .filter((s) => !isEmpty(s)),
      });
    }
    onClose();
  };

  const onRemove = async () => {
    if (curItem?.id) {
      await dispatch.svc.deleteSVC(curItem.id);
      onClose();
    }
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {curItem?.id
                ? t('platform.svc.form.title.edit')
                : t('platform.svc.form.title.create')}
            </Headline>
          }
          extra={[
            <RevxButton type="primary" brandGreen onClick={onSave}>
              {t('common.save')}
            </RevxButton>,
            <>
              <Divider type="vertical" />
              <RevxButton onClick={onClose} brandGreen>
                {t('common.cancel')}
              </RevxButton>
            </>,
            ...(curItem?.id
              ? [
                  <>
                    <Divider type="vertical" />
                    <Popconfirm
                      title={t('common.confirmQuestion')}
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      onConfirm={onRemove}
                      okText={t('common.ok')}
                      cancelText={t('common.cancel')}
                      overlayStyle={{ width: 160 }}
                      getPopupContainer={(e) => {
                        return e as HTMLElement;
                      }}
                    >
                      <RevxButton danger>{t('common.remove')}</RevxButton>
                    </Popconfirm>
                  </>,
                ]
              : []),
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );

  const [svcOptions, setSVCOptions] = useImmer<SvcOptionWithId[]>(() => {
    return (curItem?.svcOptions || []).map((item) => {
      return {
        ...item,
        withIdValues: (item.values || []).map((optValue) => {
          return {
            value: optValue,
            id: uniqueId('svcOptionValue'),
          };
        }),
        id: uniqueId('svcOption'),
      };
    });
  });

  const [hasMaterial, setHasMaterial] = useState(curItem?.hasMaterial);

  return (
    <RevxSpin spinning={saving || deleting}>
      {actionBar}
      <Tabs
        style={{ padding: '0 24px' }}
        activeKey={activeTabKey}
        size="large"
        onChange={(activeKey) => {
          setActiveTabKey(activeKey);
        }}
      >
        <TabPane key="basic" tab={t('platform.svc.form.tabBasicInfo')}>
          <div className="platform-item-form-wrapper">
            <Form
              {...PLATFORM_FORM_ITEM_LAYOUT}
              className="platform-item-form"
              layout={'horizontal'}
              form={form}
              scrollToFirstError
              initialValues={{
                ...curItem,
                active: Boolean(
                  curItem?.active === undefined ? true : curItem?.active
                ),
                hasMaterial: Boolean(curItem?.hasMaterial),
                svcTypeId: curItem?.svcType?.id,
                joinedParameterLabels: (curItem?.parameterLabels || []).join(
                  ','
                ),
              }}
            >
              <Form.Item
                label={t('common.status')}
                name="active"
                valuePropName="checked"
              >
                <Switch disabled={true} />
              </Form.Item>
              <Form.Item
                label={t('common.type')}
                name="svcTypeId"
                rules={[RULE_NUMBER, { required: true }]}
              >
                <Select
                  disabled={!isBase}
                  showSearch
                  filterOption={(inputValue, option) => {
                    return String(option?.children)
                      .toLocaleLowerCase()
                      .includes(inputValue.toLocaleLowerCase());
                  }}
                >
                  {svcTypes.map((type) => {
                    return (
                      <Select.Option value={type.id} key={type.id}>
                        {type.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Form.Item
                label={t('common.revxId')}
                name="revxId"
                rules={[RULE_INTEGER, { required: true }]}
              >
                <Input disabled={viewingSvc} />
              </Form.Item>
              <Form.Item
                label={t('platform.svc.tableColumn.name')}
                name="name"
                rules={[RULE_TEXT_LEN_255, { required: true }]}
              >
                <Input disabled={!isBase} />
              </Form.Item>
              <Form.Item label={t('platform.svc.form.image')} name="image">
                <RevxInputImage disabled={!isBase} />
              </Form.Item>

              <Form.Item
                label={t('platform.svc.tableColumn.uom')}
                name="uom"
                rules={[RULE_INTEGER, { required: true }]}
              >
                <Select disabled={!isBase}>
                  {UOMOptions.map((opt) => {
                    return (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Form.Item
                label={t('platform.svc.tableColumn.uomCoefficient')}
                name="uomCoefficient"
                rules={[RULE_TEXT_LEN_255, { required: true }]}
              >
                <Select disabled={!isBase}>
                  {UOMCoefficientOptions.map((option) => {
                    return (
                      <Select.Option key={option.value} value={option.value}>
                        {option.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>

              <Form.Item label={t('platform.svc.tableColumn.serviceMaterial')}>
                <Form.Item
                  name="hasMaterial"
                  valuePropName="checked"
                  style={{ marginBottom: hasMaterial ? 24 : 0 }}
                  rules={[{ required: true }]}
                >
                  <Switch
                    disabled={!isBase}
                    onChange={(val) => {
                      setHasMaterial(val);
                      form.setFieldsValue({
                        materialUom: 1,
                        materialUomCoefficient: UOMCoefficientEnum.JOB_QUANTITY,
                      });
                    }}
                  />
                </Form.Item>
                <div style={{ display: hasMaterial ? 'block' : 'none' }}>
                  <Form.Item>
                    <Input.Group>
                      <span className="ant-input-group-addon">
                        <span style={{ color: 'red' }}>*</span>{' '}
                        {t('platform.svc.tableColumn.materialUom')}
                      </span>

                      <Form.Item
                        name="materialUom"
                        noStyle
                        rules={[
                          RULE_INTEGER,
                          {
                            required: hasMaterial,
                            message: t('platform.svc.form.materialUomRequired'),
                          },
                        ]}
                      >
                        <Select style={{ width: '100%' }} disabled={!isBase}>
                          {UOMOptions.map((opt) => {
                            return (
                              <Select.Option key={opt.value} value={opt.value}>
                                {opt.name}
                              </Select.Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>

                  <Form.Item>
                    <Input.Group>
                      <span className="ant-input-group-addon">
                        <span style={{ color: 'red' }}>*</span>{' '}
                        {t('platform.svc.tableColumn.materialUomCoefficient')}
                      </span>
                      <Form.Item
                        name="materialUomCoefficient"
                        rules={[
                          RULE_TEXT_LEN_255,
                          {
                            required: hasMaterial,
                            message: t(
                              'platform.svc.form.materialUomCoefficientRequired'
                            ),
                          },
                        ]}
                        noStyle
                      >
                        <Select style={{ width: '100%' }} disabled={!isBase}>
                          {UOMCoefficientOptions.map((option) => {
                            return (
                              <Select.Option
                                key={option.value}
                                value={option.value}
                              >
                                {option.name}
                              </Select.Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                </div>
              </Form.Item>

              <Form.Item
                label={t('platform.svc.form.showInstructions')}
                name="showInstructions"
                valuePropName="checked"
              >
                <Switch disabled={!isBase} />
              </Form.Item>

              <Form.Item
                label={t('platform.svc.form.message')}
                name="message"
                rules={[RULE_TEXT_LEN_255]}
              >
                <Input disabled={!isBase} />
              </Form.Item>

              <Form.Item
                label={t('platform.svc.form.jobConfigViewTypes')}
                name="jobConfigViewTypes"
                rules={[{ type: 'array', required: true }]}
              >
                <Checkbox.Group disabled={!isBase}>
                  <Checkbox value="BUYER">Buyer</Checkbox>

                  <Checkbox value="EXPERT">Expert</Checkbox>
                </Checkbox.Group>
              </Form.Item>

              <Form.Item
                label={t('platform.svc.form.parameterLabels')}
                name="joinedParameterLabels"
              >
                <Input disabled={!isBase} />
              </Form.Item>
            </Form>
          </div>
        </TabPane>
        <TabPane key="options" tab={t('platform.svc.form.tabOptions')}>
          <div className="platform-item-form-wrapper">
            <div style={{ width: '30vw' }}>
              {svcOptions.map((svcOption, index) => {
                return (
                  <Card
                    style={{ marginBottom: 20 }}
                    title={
                      <SectionHeading>{`Option ${index + 1}`}</SectionHeading>
                    }
                    size="small"
                    key={svcOption.id}
                    extra={
                      <DeleteTwoTone
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          let newOptions = svcOptions.filter(
                            (opt) => opt.id !== svcOption.id
                          );

                          newOptions = newOptions.map((option) => {
                            return {
                              ...option,
                              id: uniqueId('svcOption'),
                            };
                          });

                          setSVCOptions(newOptions);
                        }}
                      />
                    }
                  >
                    <Row gutter={[12, 12]}>
                      <Col span={4}>{t('common.label')}:</Col>
                      <Col span={20}>
                        <Input
                          disabled={!isBase}
                          value={svcOption.label}
                          onChange={(e) => {
                            setSVCOptions((draft) => {
                              const opt = draft.find(
                                (item) => item.id === svcOption.id
                              );
                              if (opt) {
                                opt.label = e.target.value;
                              }
                            });
                          }}
                        />
                      </Col>
                      <Col span={4}>{t('common.type')}:</Col>
                      <Col span={20}>
                        <Select
                          disabled={!isBase}
                          style={{ width: '100%' }}
                          value={svcOption.type}
                          onChange={(optionValue) => {
                            setSVCOptions((draft) => {
                              const opt = draft.find(
                                (item) => item.id === svcOption.id
                              );
                              if (opt) {
                                opt.type = optionValue;
                              }
                            });
                          }}
                        >
                          {ServiceOptionTypeOptions.map((option) => {
                            return (
                              <Select.Option
                                key={option.value}
                                value={option.value}
                              >
                                {option.name}
                              </Select.Option>
                            );
                          })}
                        </Select>
                      </Col>
                      <Col span={4}>
                        {t('platform.svc.form.valueUom')}:
                      </Col>
                      <Col span={20}>
                        <Select
                          disabled={!isBase}
                          style={{ width: '100%' }}
                          value={
                            svcOption.valueUom ?? MeasurementQuantityEnum.NONE
                          }
                          onChange={(optionValue) => {
                            setSVCOptions((draft) => {
                              const opt = draft.find(
                                (item) => item.id === svcOption.id
                              );
                              if (opt) {
                                opt.valueUom = optionValue;
                              }
                            });
                          }}
                        >
                          {VALUE_UOM_OPTIONS.map((option) => (
                            <Select.Option
                              key={option.value}
                              value={option.value}
                            >
                              {t(option.labelKey)}
                            </Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={4}>{t('platform.svc.form.values')}:</Col>
                      <Col span={20}>
                        {(svcOption.withIdValues || []).map((optionValue) => {
                          return (
                            <Row
                              gutter={12}
                              style={{ marginBottom: 12 }}
                              key={optionValue.id}
                              align="middle"
                            >
                              <Col span={22}>
                                <Input
                                  disabled={!isBase}
                                  value={optionValue.value}
                                  onChange={(e) => {
                                    setSVCOptions((draft) => {
                                      const opt = draft.find(
                                        (item) => item.id === svcOption.id
                                      );
                                      if (opt) {
                                        const optValue = opt.withIdValues.find(
                                          (item) => item.id === optionValue.id
                                        );
                                        if (optValue) {
                                          optValue.value = e.target.value;
                                        }
                                      }
                                    });
                                  }}
                                ></Input>
                              </Col>
                              <Col span={2}>
                                <MinusCircleTwoTone
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    setSVCOptions((draft) => {
                                      const opt = draft.find(
                                        (item) => item.id === svcOption.id
                                      );
                                      if (opt) {
                                        opt.withIdValues =
                                          opt.withIdValues.filter(
                                            (item) => item.id !== optionValue.id
                                          );
                                      }
                                    });
                                  }}
                                />
                              </Col>
                            </Row>
                          );
                        })}
                        <RevxButton
                          disabled={!isBase}
                          type="dashed"
                          style={{ width: '100%' }}
                          icon={<PlusOutlined />}
                          onClick={() => {
                            const newValue = {
                              id: uniqueId('svcOptionValue'),
                              value: '',
                            };
                            setSVCOptions((draft) => {
                              const opt = draft.find(
                                (item) => item.id === svcOption.id
                              );

                              opt?.withIdValues.push(newValue);
                            });
                          }}
                        >
                          {t('platform.svc.form.addValue')}
                        </RevxButton>
                      </Col>
                    </Row>
                  </Card>
                );
              })}
              {svcOptions.length < 3 && (
                <RevxButton
                  disabled={!isBase}
                  type="dashed"
                  size="large"
                  style={{ width: '100%', marginBottom: 30 }}
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const newOption: SvcOptionWithId = {
                      id: uniqueId('svcOption'),
                      label: '',
                      defaultValue: '',
                      values: [],
                      withIdValues: [],
                      type: ServiceOptionTypeEnum.RADIO,
                    };
                    setSVCOptions((draft) => {
                      draft.push(newOption);
                    });
                  }}
                >
                  {t('platform.svc.form.addOption')}
                </RevxButton>
              )}
            </div>
          </div>
        </TabPane>
      </Tabs>
    </RevxSpin>
  );
};
export default SVCItemForm;
function collectOptions(svcOptions: SvcOptionWithId[]): SvcOption[] {
  const opts = svcOptions.map((opt) => {
    return {
      ...opt,
      values: (opt.withIdValues || [])
        .filter((idValue) => {
          if (idValue.value) {
            return true;
          } else {
            return false;
          }
        })
        .map((idValue) => idValue.value),
    };
  });

  return opts
    .filter((opt) => {
      if (opt.label && opt.values.length > 0) {
        return true;
      } else {
        return false;
      }
    })
    .map((opt) => ({
      ...opt,
      defaultValue: opt.values[0],
    }));
}
